// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { InterventionsService } from 'src/app/modules/interventions/interventions.service';
import { IReceivedDocument } from 'src/app/shared/models/intervention/intervention.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-edit-receivedDocument-dialog',
  templateUrl: './edit-receivedDocument-dialog.component.html',
  styleUrls: ['./edit-receivedDocument-dialog.component.scss']
})
export class EditReceivedDocumentDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Edit receivedDocument form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: IReceivedDocument = {
    name: "",
  };
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.save'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-save',
      inline: true,
    },
  };
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.delete'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'warn',
    spinnerColor: 'warn',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-trash',
      inline: true,
    },
  };
  // END - Edit receivedDocument form

  dialogIsLoading = true;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<EditReceivedDocumentDialogComponent>,
    public deleteDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private interventionsService: InterventionsService,
    private authService: AuthService,
    private helperService: HelperService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   * @returns undefined
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Init edit form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @returns undefined
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {
      name: this.data.receivedDocument.name,
      description: this.data.receivedDocument.description,
    };

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'name',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.name'),
              placeholder: this.translateService.instant('interventions.fields.name'),
              required: true,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'description',
            templateOptions: {
              rows: 4,
              label: this.translateService.instant('interventions.fields.description'),
              placeholder: this.translateService.instant('interventions.fields.description'),
              required: false,
              maxLength: 5000,
            },
            expressionProperties: {
              'templateOptions.description': '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            }
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   * @author Francesco Bancalari <francesco.bancalari@sitisrl.it>
   *
   * @param [data] optional data passed to the caller component
   * @returns void
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.interventionsService.updateReceivedDocument(this.data.interventionId, this.data.receivedDocument._id, this.model)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (
              res.status
              && res.updatedIntervention
              && res.updatedIntervention.receivedDocuments
            ) {
              this.toastrService.success(res.message);
              this.onClose({ updatedIntervention: res.updatedIntervention });
              return;
            }
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Delete current receivedDocument
   *
   * @since 1.0.0
   */
  // onDelete(): void {
  //   // Activate delete button loading status if not activated yet
  //   if (this.deleteBtnOptions.active) {
  //     return;
  //   }
  //   this.deleteBtnOptions.active = true;

  //   // Init dialog content
  //   const deleteDialogRef = this.deleteDialog.open(ConfirmDialogComponent, {
  //     data: {
  //       title: this.translateService.instant('dialogs.confirm.title'),
  //       message: this.translateService.instant('dialogs.confirm.removeMessage'),
  //       btnOkText: this.translateService.instant('common.cta.confirm'),
  //       btnCancelText: this.translateService.instant('common.cta.undo'),
  //     },
  //     width: '500px',
  //     disableClose: true,
  //   });

  //   // Subscribe to dialog result (only for 1 emit thanks to "take()")
  //   deleteDialogRef.beforeClosed()
  //     .pipe(
  //       take(1)
  //     )
  //     .subscribe(
  //       res => {
  //         if (!res) {
  //           this.deleteBtnOptions.active = false;
  //           return;
  //         }
  //         this.subscriptions.push(
  //           this.interventionsService.deleteReceivedDocument(this.data.interventionId, this.data.receivedDocument._id)
  //             .pipe(
  //               finalize(() => this.deleteBtnOptions.active = false)
  //             )
  //             .subscribe(
  //               res => {
  //                 if (res.status) {
  //                   this.toastrService.success(res.message);
  //                   this.onClose({ deletedReceivedDocument: true });
  //                   this.deleteBtnOptions.active = false;
  //                   return;
  //                 }
  //                 this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
  //               },
  //               err => {
  //                 // Choose one of the following error handling
  //                 // method. The first one show a message right
  //                 // under the form fields (if the form is properly
  //                 // setted), the second one show toastr
  //                 // notifications for each error
  //                 // this.helperService.handleFormError(this.form, err);
  //                 this.helperService.handleError(err);
  //                 this.onClose();
  //               }
  //             )
  //         );
  //       }
  //     );
  // }
}
